import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle } from 'lucide-react'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { useForm } from 'react-hook-form'

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/elements/form'
import { Input } from '@/components/elements/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/elements/select'
import { Textarea } from '@/components/elements/textarea'
import { FileUploader } from '@/components/FileUploader'
import { Modal } from '@/components/Modal'
import { updateCourseAsset } from '@/services/course_assets.service'
import { toast } from '@/utils/helpers'

// Accepted upload MIME types per asset type when replacing the file. Unlike
// create (Video/Articulate only), edit covers every asset type so an existing
// PDF or PowerPoint can still have its file swapped. Unknown types fall back to
// accepting any file.
const ASSET_FILE_TYPES = {
  Video: ['video/mp4', 'video/webm'],
  Articulate: ['application/zip'],
  PDF: ['application/pdf'],
  PowerPoint: [
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  ],
}

const EditAssetModal = ({ entityId, organizationId, asset, close }) => {
  const form = useForm({
    defaultValues: {
      name: asset.name ?? '',
      description: asset.description ?? '',
      filePath: asset.filePath ?? '',
      fileDisplayName: asset.fileDisplayName ?? '',
      videoUrl: asset.videoUrl ?? '',
      // UI-only: an existing Video asset is a link if it has a video_url,
      // otherwise it's an uploaded file.
      videoSource: asset.videoUrl ? 'url' : 'upload',
    },
  })
  const queryClient = useQueryClient()

  // While a replacement file is still uploading to S3, filePath holds the OLD
  // path — submitting mid-upload would silently save the previous file. Track
  // the in-flight upload so Save Changes can be disabled until it settles
  // (RSCX-363).
  const [isUploading, setIsUploading] = useState(false)

  // Type is locked on edit — changing it would swap content out from under any
  // content block that references this asset by type.
  const { type } = asset
  const acceptedFileTypes = ASSET_FILE_TYPES[type]

  // S3 prefix segregates uploads by owning scope.
  const filePrefix = (() => {
    if (organizationId) return `course-assets/org-${organizationId}/`
    if (entityId) return `course-assets/${entityId}/`
    return 'course-assets/global/'
  })()

  const name = form.watch('name')
  const filePath = form.watch('filePath')
  const videoUrl = form.watch('videoUrl')
  const videoSource = form.watch('videoSource')

  const isVideoUrl = type === 'Video' && videoSource === 'url'
  const inUseCount = asset.courseCount ?? 0
  // Require a name, plus content appropriate to the chosen source. filePath is
  // pre-filled for file-backed assets, so this only blocks submission if the
  // user clears it by switching a video from upload → link with no URL (or
  // link → upload without re-uploading).
  const canSubmit =
    Boolean(name?.trim()) && (isVideoUrl ? Boolean(videoUrl?.trim()) : Boolean(filePath))

  const { isPending, mutate: mutateAsset } = useMutation({
    mutationFn: (data) => updateCourseAsset({ entityId, organizationId, id: asset.id, ...data }),
    onError: (err) => toast(err.message, 'error'),
    onSuccess: (data) => {
      toast(`${data.name} updated`, 'success')
      queryClient.invalidateQueries({
        queryKey: ['course-assets', entityId ?? organizationId ?? null],
      })
      close()
    },
  })

  const onSubmit = ({ videoSource: _src, ...data }) => {
    // Guard the form's native submit path (Enter key) the same way the
    // Save Changes button is disabled while an upload is in flight.
    if (isUploading) return
    // Send exactly one of file_path / video_url depending on the chosen source.
    // Sending an unchanged file_path is a no-op on the backend (Articulate is
    // only re-processed when the path actually changes).
    if (isVideoUrl) {
      mutateAsset({
        ...data,
        type,
        filePath: '',
        fileDisplayName: '',
        videoUrl: data.videoUrl.trim(),
      })
    } else {
      mutateAsset({ ...data, type, videoUrl: '' })
    }
  }

  const renderInUseWarning = (what) =>
    inUseCount > 0 && (
      <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        <span>
          Used by {inUseCount} course module(s). Replacing the {what} changes the content those
          modules show.
        </span>
      </div>
    )

  return (
    <Modal
      actions={[
        { label: 'Cancel', onClick: close, type: 'cancel' },
        {
          label: isUploading ? 'Uploading…' : 'Save Changes',
          onClick: form.handleSubmit(onSubmit),
          type: 'submit',
          disabled: !canSubmit || isUploading,
        },
      ]}
      content={
        <Form {...form}>
          <form className="flex flex-col gap-4" onSubmit={form.handleSubmit(onSubmit)}>
            <FormField
              control={form.control}
              disabled={isPending}
              name="name"
              rules={{ required: 'Name is required' }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              disabled={isPending}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea rows={3} {...field} />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormItem>
              <FormLabel>Type</FormLabel>
              <Input value={type} disabled readOnly />
            </FormItem>

            {type === 'Video' && (
              <FormField
                control={form.control}
                name="videoSource"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Video source</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="upload">Upload a file</SelectItem>
                        <SelectItem value="url">Link (YouTube, Vimeo, etc.)</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />
            )}

            {isVideoUrl && (
              <>
                {renderInUseWarning('link')}
                <FormField
                  control={form.control}
                  disabled={isPending}
                  name="videoUrl"
                  rules={{
                    required: 'Enter a video URL',
                    pattern: { value: /^https?:\/\/.+/i, message: 'Enter a valid URL' },
                  }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Video URL</FormLabel>
                      <FormControl>
                        <Input placeholder="https://www.youtube.com/watch?v=…" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            )}

            {!isVideoUrl && (
              <FormItem>
                <FormLabel>File</FormLabel>
                {renderInUseWarning('file')}
                {filePath && (
                  <p className="text-muted-foreground text-xs">
                    Current file: {form.watch('fileDisplayName') || filePath}
                  </p>
                )}
                <FileUploader
                  acceptedFileTypes={acceptedFileTypes}
                  filePrefix={filePrefix}
                  type="aws"
                  maxFiles={1}
                  handleUploadToServer={async (file) => {
                    form.setValue('filePath', file.fileUploadName, { shouldDirty: true })
                    form.setValue('fileDisplayName', file.fileName, { shouldDirty: true })
                  }}
                  // FilePond lifecycle → isUploading: a file entering the pond
                  // starts an instant upload; processed / aborted / errored all
                  // settle it. Keeps Save Changes disabled mid-upload.
                  onaddfilestart={() => setIsUploading(true)}
                  onprocessfile={() => setIsUploading(false)}
                  onprocessfileabort={() => setIsUploading(false)}
                  onerror={() => setIsUploading(false)}
                />
                <p className="text-muted-foreground text-xs">
                  Upload a new file to replace the current one, or leave it to keep the existing
                  file.
                </p>
              </FormItem>
            )}
          </form>
        </Form>
      }
      loading={isPending}
      title="Edit Course Asset"
      setOpen={close}
    />
  )
}

EditAssetModal.propTypes = {
  entityId: PropTypes.string,
  organizationId: PropTypes.string,
  asset: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    description: PropTypes.string,
    type: PropTypes.string,
    filePath: PropTypes.string,
    fileDisplayName: PropTypes.string,
    videoUrl: PropTypes.string,
    courseCount: PropTypes.number,
  }).isRequired,
  close: PropTypes.func.isRequired,
}

export default EditAssetModal
